import apiClient from './apiClient';
import type { VisitWithContext, VisitFormState } from '@app-types/visit';
import { fromApiVisit, toApiVisitForm, type ApiVisit } from '@utils/visitDtoMappers';

export interface Visit {
  id: string;
  clinicId: string;
  petId: string;
  ownerId: string;
  doctorId: string;
  reason: string;
  status: 'in-progress' | 'completed' | 'cancelled' | 'locked' | 'draft' | 'signed';
  startTime: string;
  endTime?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Additional fields for compatibility
  created_at?: string;
  visit_type?: string;
  visit_date?: string;
  chief_complaint?: string;
  signed_at?: string;
  signed_by?: string;
  signature_notes?: string;
  locked_at?: string;
  history?: string;
  physical_exam?: string;
  diagnosis?: string;
  treatment_plan?: string;
  medications?: string;
  recommendations?: string;
  follow_up_notes?: string;
  /** Backend: timp start consult (draft activ) */
  started_at?: string | null;
  cancelled_at?: string | null;
  internal_notes?: string | null;
  visit_duration?: number;
  cost?: number;
}

export interface CreateVisitData {
  clinicId: string;
  petId: string;
  ownerId: string;
  doctorId: string;
  reason: string;
  notes?: string;
  appointmentId?: string;
  visitType?: string;
  visitDate?: string;
  /** Opționale: același contract ca POST /visits (workflow unificat). */
  physical_exam?: string;
  diagnosis?: string;
  treatment_plan?: string;
  recommendations?: string;
  follow_up_notes?: string;
  vital_signs?: Record<string, unknown>;
}

export interface CreateAddendumData {
  reason: string;
  content: string;
  type: 'correction' | 'completion' | 'laboratory_result' | 'prescription_void' | 'other';
  notify_owner?: boolean;
  metadata?: Record<string, unknown>;
}

export interface UpdateVisitFieldsPayload {
  visit_type?: string;
  chief_complaint?: string;
  history?: string;
  physical_exam?: string;
  diagnosis?: string;
  treatment_plan?: string;
  medications?: string;
  recommendations?: string;
  follow_up_notes?: string;
  internal_notes?: string;
  vital_signs?: Record<string, unknown>;
}

export interface VisitAddendum {
  id: string;
  visitId: string;
  content: string;
  type: string;
  attachments: string[];
  createdAt: string;
  createdBy: string;
  // Additional fields for compatibility
  created_at?: string;
  author_vet_id?: string;
  reason?: string;
  metadata?: any;
}

export interface VisitWithAddenda {
  visit: Visit;
  addenda: VisitAddendum[];
}

export interface CanEditResponse {
  canEdit: boolean;
  reason?: string;
  lockedAt?: string;
  timeUntilLock?: number;
}

class VisitsService {
  async createVisit(visitData: CreateVisitData): Promise<Visit> {
    try {
      const payload: Record<string, unknown> = {
        patient_id: visitData.petId,
        clinic_id: visitData.clinicId,
        veterinarian_id: visitData.doctorId,
        appointment_id: visitData.appointmentId,
        visit_date: visitData.visitDate || new Date().toISOString(),
        visit_type: visitData.visitType || 'consultation',
        chief_complaint: visitData.reason,
        history: visitData.notes || ''
      };
      if (visitData.physical_exam !== undefined) payload.physical_exam = visitData.physical_exam;
      if (visitData.diagnosis !== undefined) payload.diagnosis = visitData.diagnosis;
      if (visitData.treatment_plan !== undefined) payload.treatment_plan = visitData.treatment_plan;
      if (visitData.recommendations !== undefined) payload.recommendations = visitData.recommendations;
      if (visitData.follow_up_notes !== undefined) payload.follow_up_notes = visitData.follow_up_notes;
      if (visitData.vital_signs !== undefined) payload.vital_signs = visitData.vital_signs;

      const response = await apiClient.post('/visits', payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create visit');
      }
    } catch (error) {
      console.error('Error creating visit:', error);
      throw error;
    }
  }

  /**
   * PATCH câmpuri pe rândul `visits` (SOAP, recomandări, note interne).
   */
  async updateVisitFields(visitId: string, payload: UpdateVisitFieldsPayload): Promise<Visit> {
    const response = await apiClient.patch(`/visits/${visitId}`, payload);
    if (response.data?.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data?.message || response.data?.error || 'Eroare la actualizarea vizitei');
  }

  /** Sumar vizibil pentru proprietar (fără note interne). */
  async getOwnerSafeVisitSummary(visitId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/visits/${visitId}/owner-summary`);
    if (response.data?.success && response.data.data) {
      return response.data.data as Record<string, unknown>;
    }
    throw new Error(response.data?.message || 'Nu s-a putut încărca sumarul vizitei');
  }

  async getVisitWithContext(visitId: string): Promise<VisitWithContext> {
    try {
      const response = await apiClient.get(`/visits/${visitId}/context`);

      if (response.data.success) {
        // Folosește mapper pentru a converti din snake_case la camelCase
        const apiVisit = response.data.data as ApiVisit;
        return fromApiVisit(apiVisit);
      } else {
        throw new Error(response.data.error || 'Failed to fetch visit context');
      }
    } catch (error) {
      console.error('Error fetching visit context:', error);
      throw error;
    }
  }

  // Backward compatible: param numit petId, dar endpointul corect cere patientId
  async getVisitsForPet(petId: string): Promise<Visit[]> {
    try {
      // Endpoint corect: /visits/patient/:patientId
      const response = await apiClient.get(`/visits/patient/${petId}`);

      return response.data.data || [];
    } catch (error: any) {
      // Nu considerăm lipsa vizitelor un caz de eroare; întoarcem listă goală fără log
      if (error?.response?.status === 404) {
        return [];
      }
      console.error('Error fetching visits for pet:', error);
      throw error;
    }
  }

  // Alias semantic
  async getVisitsForPatient(patientId: string): Promise<Visit[]> {
    return this.getVisitsForPet(patientId);
  }

  async checkOpenVisit(petId: string): Promise<Visit | null> {
    try {
      const response = await apiClient.get(`/visits/check-open?petId=${petId}`);

      const payload = response.data.data;
      if (!payload) return null;
      if (payload.openVisits && payload.openVisits.length > 0) {
        return payload.openVisits[0];
      }
      if (payload.hasOpenVisit && payload.visit) {
        return payload.visit;
      }
      return null;
    } catch (error) {
      console.error('Error checking open visit:', error);
      throw error;
    }
  }

  async startVisit(visitId: string): Promise<Visit> {
    try {
      const response = await apiClient.post(`/visits/${visitId}/start`, {});
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error || response.data.message || 'Failed to start visit');
    } catch (error) {
      console.error('Error starting visit:', error);
      throw error;
    }
  }

  async getVisitsForClinic(clinicId: string, limit: number = 50): Promise<Visit[]> {
    try {
      const response = await apiClient.get(`/visits/clinic/${clinicId}?limit=${limit}`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching visits for clinic:', error);
      throw error;
    }
  }

  async updateVisitForm(visitId: string, formData: Partial<VisitFormState>): Promise<Visit> {
    try {
      // Convertește formData din camelCase la snake_case pentru API
      const apiFormData = toApiVisitForm(formData as VisitFormState);
      
      const response = await apiClient.patch(`/visits/${visitId}/form`, apiFormData);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update visit form');
      }
    } catch (error) {
      console.error('Error updating visit form:', error);
      throw error;
    }
  }

  async addAddendum(visitId: string, data: CreateAddendumData): Promise<VisitAddendum> {
    const response = await apiClient.post(`/visits/${visitId}/addenda`, {
      reason: data.reason,
      content: data.content,
      type: data.type,
      notify_owner: data.notify_owner ?? true,
      metadata: data.metadata,
    });

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.error || response.data.message || 'Eroare la adăugarea addendum-ului');
  }

  async getVisitWithAddenda(visitId: string): Promise<VisitWithAddenda> {
    try {
      const response = await apiClient.get(`/visits/${visitId}/with-addenda`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch visit with addenda');
      }
    } catch (error) {
      console.error('Error fetching visit with addenda:', error);
      throw error;
    }
  }

  async signVisit(visitId: string, signatureNotes?: string): Promise<Visit> {
    const response = await apiClient.post(`/visits/${visitId}/sign`, {
      signature_notes: signatureNotes,
    });

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || response.data.error || 'Eroare la semnarea vizitei');
  }

  async canEditVisit(visitId: string): Promise<CanEditResponse> {
    try {
      const response = await apiClient.get(`/visits/${visitId}/can-edit`);

      return response.data.data;
    } catch (error) {
      console.error('Error checking if visit can be edited:', error);
      throw error;
    }
  }

  async uploadAttachment(visitId: string, file: File): Promise<{ id: string; url: string; cdnUrl: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/visits/${visitId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to upload attachment');
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  }

  async deleteAttachment(visitId: string, attachmentId: string): Promise<void> {
    try {
      await apiClient.delete(`/visits/${visitId}/attachments/${attachmentId}`);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  }

  /**
   * P0 Inventory: Administreaza o prescriptie - consuma stoc din inventar
   */
  async administerPrescription(visitId: string, prescriptionId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/visits/${visitId}/prescriptions/${prescriptionId}/administer`, {});

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || response.data.error || 'Eroare la administrarea prescriptiei');
      }
    } catch (error: any) {
      const err = error?.response?.data;
      if (err?.code === 'INSUFFICIENT_STOCK') {
        const e = new Error(err.message || 'Stoc insuficient');
        (e as any).code = 'INSUFFICIENT_STOCK';
        (e as any).available = err.available;
        (e as any).requested = err.requested;
        throw e;
      }
      throw error;
    }
  }

  /**
   * P0 Inventory: Completeaza o procedura - consuma BOM din inventar
   */
  async completeProcedure(visitId: string, procedureId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/visits/${visitId}/procedures/${procedureId}/complete`, {});

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || response.data.error || 'Eroare la completarea procedurii');
      }
    } catch (error: any) {
      const err = error?.response?.data;
      if (err?.code === 'INSUFFICIENT_STOCK') {
        const e = new Error(err.message || 'Stoc insuficient');
        (e as any).code = 'INSUFFICIENT_STOCK';
        (e as any).available = err.available;
        (e as any).requested = err.requested;
        throw e;
      }
      throw error;
    }
  }

  /**
   * P0 Inventory: Anuleaza vizita si reverteaza consumurile din inventar
   */
  async cancelVisit(visitId: string): Promise<{ reverted: number }> {
    try {
      const response = await apiClient.post(`/visits/${visitId}/cancel`, {});

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || response.data.error || 'Eroare la anularea vizitei');
      }
    } catch (error) {
      console.error('Error cancelling visit:', error);
      throw error;
    }
  }
}

export const visitsService = new VisitsService();
